import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { KYCStatusBadge } from '../../components/common/StatusBadge';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  system_administrator: 'System Administrator',
  customer_support: 'Customer Support',
  credit_analyst: 'Credit Analyst',
  risk_officer: 'Risk Officer',
  operations_officer: 'Operations Officer',
  finance_officer: 'Finance Officer',
  compliance_officer: 'Compliance Officer',
  auditor: 'Auditor',
  api_administrator: 'API Administrator',
};

const TIER_COLORS = {
  basic: { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' },
  standard: { bg: 'rgba(91,142,255,0.15)', color: '#5B8EFF' },
  gold: { bg: 'rgba(245,166,35,0.15)', color: '#F5A623' },
  premium: { bg: 'rgba(129,140,248,0.15)', color: '#818CF8' },
};

const MODULE_COLORS = {
  loans: '#5B8EFF', customers: '#00C896', disbursements: '#818CF8',
  fraud: '#FF4757', kyc: '#F5A623', system: '#ff9900',
};

const REVIEWER_META = {
  maker:   { label: 'Maker',   bg: 'rgba(0,200,150,0.12)',   color: '#00C896' },
  checker: { label: 'Checker', bg: 'rgba(129,140,248,0.12)', color: '#818CF8' },
  both:    { label: 'M + C',   bg: 'rgba(245,166,35,0.12)',  color: '#F5A623' },
  auditor: { label: 'Auditor', bg: 'rgba(91,142,255,0.12)',  color: '#5B8EFF' },
};

function ReviewerTypeBadge({ type }) {
  if (!type) return <span style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>—</span>;
  const m = REVIEWER_META[type] || { label: type, bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, padding: '0.15rem 0.5rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: m.bg, color: m.color,
    }}>
      {m.label}
    </span>
  );
}

function TierBadge({ tier }) {
  const style = TIER_COLORS[tier] || TIER_COLORS.basic;
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: style.bg, color: style.color,
    }}>
      {tier || 'basic'}
    </span>
  );
}

function RoleChip({ roleName }) {
  const module = roleName?.split('_')[0];
  const color = MODULE_COLORS[module] || '#5B8EFF';
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 600, padding: '0.15rem 0.45rem',
      borderRadius: 99, background: `${color}18`, color,
      border: `1px solid ${color}30`, whiteSpace: 'nowrap',
    }}>
      {ROLE_LABELS[roleName] || roleName}
    </span>
  );
}

function RoleModal({ user, onClose }) {
  const qc = useQueryClient();
  const [selectedRole, setSelectedRole] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [reviewerType, setReviewerType] = useState(user.reviewer_type || '');
  const [reviewerMsg, setReviewerMsg] = useState('');

  const reviewerTypeMutation = useMutation({
    mutationFn: (type) => adminService.setReviewerType(user.id, type || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setReviewerMsg('Saved.');
      setTimeout(() => setReviewerMsg(''), 2500);
    },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to update reviewer type.'),
  });

  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => adminService.getRoles().then(r => r.data.data || r.data),
  });
  const roles = rolesData?.results || rolesData || [];

  const assignMutation = useMutation({
    mutationFn: (d) => adminService.assignRole(user.id, d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      if (r.data.approval_ref) {
        setResult({ ref: r.data.approval_ref, msg: r.data.message });
      } else {
        onClose();
      }
    },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to assign role.'),
  });

  const revokeMutation = useMutation({
    mutationFn: ({ roleId }) => adminService.revokeRole(user.id, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: (e) => setErr(e.response?.data?.message || 'Failed to revoke role.'),
  });

  const handleAssign = () => {
    if (!selectedRole) return;
    const payload = { role: selectedRole };
    if (expiresAt) payload.expires_at = expiresAt;
    assignMutation.mutate(payload);
  };

  const currentRoles = user.admin_roles || [];

  if (result) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420, textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✓</div>
        <h2 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.5rem' }}>Submitted for Checker Review</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{result.msg}</p>
        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 4 }}>Approval Reference</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '1rem' }}>{result.ref}</div>
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 480, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>Manage Admin Roles</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          {user.first_name} {user.last_name} — {user.email}
        </p>

        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <span>⚖</span>
          <span style={{ fontSize: '0.8rem' }}>Assigning high-privilege roles creates a maker-checker request requiring a second admin to approve.</span>
        </div>

        {currentRoles.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-2)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Current Roles</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {currentRoles.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.6rem', background: 'var(--surface-alt)', borderRadius: 6 }}>
                  <div>
                    <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{ROLE_LABELS[r.role_name] || r.role_name}</span>
                    {r.expires_at && <span style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginLeft: '0.5rem' }}>expires {formatDate(r.expires_at)}</span>}
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', color: '#FF4757' }}
                    onClick={() => revokeMutation.mutate({ roleId: r.id })}>
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Assign New Role</label>
          <select className="form-input" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
            <option value="">— Select a role —</option>
            {Object.entries(ROLE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Expires At <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
          <input className="form-input" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
        </div>

        {/* ── Reviewer / Auditor type ── */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-2)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
            Reviewer / Auditor Type
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.75rem' }}>
            Controls this user's role in the maker-checker workflow and audit access.
          </p>

          {user.role === 'admin' && (
            <div style={{
              padding: '0.6rem 0.75rem', marginBottom: '0.75rem',
              background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)',
              borderRadius: 8, fontSize: '0.78rem', color: '#FF4757',
            }}>
              ⚠ Admin users cannot be assigned as Maker or Checker. Use a staff account for workflow roles.
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <select
              className="form-input"
              style={{ flex: 1 }}
              value={reviewerType}
              onChange={e => setReviewerType(e.target.value)}
            >
              <option value="">— None (unrestricted admin) —</option>
              <option value="maker"   disabled={user.role === 'admin'}>Maker — submits decisions for checker approval</option>
              <option value="checker" disabled={user.role === 'admin'}>Checker — gives final approval in Approval Queue</option>
              <option value="both"    disabled={user.role === 'admin'}>Both — maker and checker privileges</option>
              <option value="auditor">Auditor — read-only access to audit dashboard</option>
            </select>
            <button
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
              disabled={reviewerTypeMutation.isPending}
              onClick={() => reviewerTypeMutation.mutate(reviewerType)}
            >
              {reviewerTypeMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          {reviewerMsg && (
            <div style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '0.4rem' }}>✓ {reviewerMsg}</div>
          )}
        </div>

        {err && <div className="alert alert-error"><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={!selectedRole || assignMutation.isPending} onClick={handleAssign}>
            {assignMutation.isPending ? 'Assigning…' : 'Assign Role'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.875rem', color: 'var(--text-1)', wordBreak: 'break-all' }}>{value || '—'}</div>
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '0.35rem', marginBottom: '0.75rem' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>{children}</div>
    </div>
  );
}

function UserProfileModal({ userId, onClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-user-detail', userId],
    queryFn: () => adminService.getUser(userId).then(r => r.data.data),
  });

  const u = data;

  const completionColor = u?.profile_completion >= 80 ? '#00C896' : u?.profile_completion >= 50 ? '#F5A623' : '#FF4757';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: 680, maxHeight: '92vh', overflow: 'auto', padding: '1.5rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {u?.profile_photo ? (
              <img src={`${API_BASE.replace('/api', '')}${u.profile_photo}`} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)' }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', color: 'var(--primary)', fontWeight: 700, border: '2px solid var(--border)' }}>
                {u ? `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}` : '…'}
              </div>
            )}
            <div>
              {isLoading ? <div style={{ color: 'var(--text-2)', fontSize: '0.875rem' }}>Loading…</div> : (
                <>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{u?.first_name} {u?.last_name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{u?.email}</div>
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                    <TierBadge tier={u?.tier} />
                    <KYCStatusBadge status={u?.kyc_status} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: 99, textTransform: 'uppercase', background: u?.is_active ? 'rgba(0,200,150,0.12)' : 'rgba(255,71,87,0.12)', color: u?.is_active ? '#00C896' : '#FF4757' }}>
                      {u?.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>

        {isError && <div className="alert alert-error"><span>⚠</span><span>Failed to load profile.</span></div>}

        {u && (
          <>
            {/* Profile completion bar */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-2)', fontWeight: 600 }}>Profile Completion</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: completionColor }}>{u.profile_completion}%</span>
              </div>
              <div style={{ height: 5, background: 'var(--surface-alt)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${u.profile_completion}%`, background: completionColor, borderRadius: 99, transition: 'width 0.4s ease' }} />
              </div>
            </div>

            <ProfileSection title="Personal Information">
              <InfoRow label="Phone" value={u.phone_number} />
              <InfoRow label="Date of Birth" value={u.date_of_birth ? formatDate(u.date_of_birth) : null} />
              <InfoRow label="Address" value={u.address} />
              <InfoRow label="State" value={u.state} />
              <InfoRow label="Country" value={u.country} />
              <InfoRow label="Email Verified" value={u.is_email_verified ? '✓ Yes' : '✗ No'} />
              <InfoRow label="Phone Verified" value={u.is_phone_verified ? '✓ Yes' : '✗ No'} />
              <InfoRow label="Member Since" value={formatDate(u.created_at)} />
            </ProfileSection>

            <ProfileSection title="Next of Kin">
              <InfoRow label="Name" value={u.next_of_kin_name} />
              <InfoRow label="Phone" value={u.next_of_kin_phone} />
              <InfoRow label="Relationship" value={u.next_of_kin_relationship} />
            </ProfileSection>

            {u.kyc_details && (
              <ProfileSection title="KYC Details">
                <InfoRow label="Status" value={u.kyc_details.status?.replace(/_/g, ' ')} />
                <InfoRow label="ID Type" value={u.kyc_details.id_type?.replace(/_/g, ' ')} />
                <InfoRow label="BVN Verified" value={u.kyc_details.bvn_verified ? '✓ Yes' : '✗ No'} />
                <InfoRow label="NIN Verified" value={u.kyc_details.nin_verified ? '✓ Yes' : '✗ No'} />
                <InfoRow label="Submitted" value={u.kyc_details.submitted_at ? formatDate(u.kyc_details.submitted_at) : null} />
                <InfoRow label="Reviewed" value={u.kyc_details.reviewed_at ? formatDate(u.kyc_details.reviewed_at) : null} />
                {u.kyc_details.rejection_reason && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <InfoRow label="Rejection Reason" value={u.kyc_details.rejection_reason} />
                  </div>
                )}
              </ProfileSection>
            )}

            <ProfileSection title="Account">
              <InfoRow label="Role" value={u.role} />
              <InfoRow label="Tier" value={u.tier} />
              <InfoRow label="Total Loans" value={String(u.loan_count ?? 0)} />
              <InfoRow label="Last Updated" value={formatDate(u.updated_at)} />
            </ProfileSection>

            {u.admin_roles?.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '0.35rem', marginBottom: '0.75rem' }}>Admin Roles</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {u.admin_roles.map(r => <RoleChip key={r.id} roleName={r.role_name} />)}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: '1.25rem', textAlign: 'right' }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function UsersList() {
  const qc = useQueryClient();
  const { isMaker, isChecker } = useAuth();
  const isRestrictedRole = isMaker || isChecker;
  const [search, setSearch] = useState('');
  const [roleModal, setRoleModal] = useState(null);
  const [profileModal, setProfileModal] = useState(null);
  const [err, setErr] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => adminService.getUsers(search ? { search } : {}).then(r => r.data.data),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => adminService.updateUserStatus(id, { is_active }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setErr(''); },
    onError: (e) => setErr(e.response?.data?.message || 'Status update failed.'),
  });

  const allUsers = data?.results || data || [];
  const users = isRestrictedRole ? allUsers.filter(u => u.role === 'customer') : allUsers;

  return (
    <div>
      <div className="page-header">
        <h1>Customer Management</h1>
        <p>View and manage customer accounts, tiers, and admin roles</p>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span><span>{err}</span></div>}

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <input
          className="form-input"
          style={{ maxWidth: 320 }}
          placeholder="Search by name, email, or phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div style={{ padding: '2rem' }}><LoadingSpinner /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Tier</th>
                  <th>KYC</th>
                  {!isRestrictedRole && <th>Admin Roles</th>}
                  {!isRestrictedRole && <th>Reviewer</th>}
                  <th>Loans</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No users found.</td></tr>
                ) : users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{u.first_name} {u.last_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{u.email}</div>
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>{u.phone_number || '—'}</td>
                    <td><TierBadge tier={u.tier} /></td>
                    <td><KYCStatusBadge status={u.kyc_status} /></td>
                    {!isRestrictedRole && (
                      <td>
                        {u.admin_roles?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', maxWidth: 200 }}>
                            {u.admin_roles.slice(0, 2).map(r => <RoleChip key={r.id} roleName={r.role_name} />)}
                            {u.admin_roles.length > 2 && (
                              <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>+{u.admin_roles.length - 2} more</span>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>—</span>
                        )}
                      </td>
                    )}
                    {!isRestrictedRole && <td><ReviewerTypeBadge type={u.reviewer_type} /></td>}
                    <td style={{ fontSize: '0.875rem', textAlign: 'center' }}>{u.loan_count ?? 0}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(u.created_at)}</td>
                    <td>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: 99, textTransform: 'uppercase',
                        background: u.is_active ? 'rgba(0,200,150,0.12)' : 'rgba(255,71,87,0.12)',
                        color: u.is_active ? '#00C896' : '#FF4757',
                      }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {!isRestrictedRole && (
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem', color: u.is_active ? '#FF4757' : '#1596E0', whiteSpace: 'nowrap' }}
                            disabled={toggleMutation.isPending}
                            onClick={() => toggleMutation.mutate({ id: u.id, is_active: !u.is_active })}>
                            {u.is_active ? 'Suspend' : 'Activate'}
                          </button>
                        )}
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem', whiteSpace: 'nowrap' }}
                          onClick={() => setProfileModal(u.id)}>
                          Profile
                        </button>
                        {!isRestrictedRole && (
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem', whiteSpace: 'nowrap' }}
                            onClick={() => setRoleModal(u)}>
                            Roles
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {profileModal && <UserProfileModal userId={profileModal} onClose={() => setProfileModal(null)} />}
      {roleModal && <RoleModal user={roleModal} onClose={() => setRoleModal(null)} />}
    </div>
  );
}
